/**
 * @file ServerOrders.jsx
 * @description Appointment-style card list showing orders grouped by server
 */

import React, { useEffect, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import { apiFetch } from '../../../utils/api'; // Make sure this path is correct

const ServerOrders = ({ siteId = "all", locationId = "all", sublocationId = "all" }) => {
  const [serverData, setServerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchServerOrders();
  }, [siteId, locationId, sublocationId]);

  const getLast30DayRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);

    const toYmd = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    return { startDate: toYmd(start), endDate: toYmd(end) };
  };

  const fetchServerOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      const { startDate, endDate } = getLast30DayRange();

      if (siteId && siteId !== "all") params.append("site_id", String(siteId));
      if (locationId && locationId !== "all") params.append("location_id", String(locationId));
      if (sublocationId && sublocationId !== "all") params.append("sublocation_id", String(sublocationId));
      params.append("startDate", startDate);
      params.append("endDate", endDate);

      const query = params.toString();
      const res = await apiFetch(`/updates-reports/orders-by-server${query ? `?${query}` : ""}`);

      if (!res.ok) {
        throw new Error(`HTTP ${res.status} - ${res.statusText}`);
      }

      const data = await res.json();

      // Ensure we only set arrays
      if (Array.isArray(data)) {
        setServerData(data);
      } else {
        console.warn('⚠️ Unexpected API response:', data);
        setServerData([]); // fallback to empty list
      }
    } catch (err) {
      console.error('❌ Failed to fetch server orders:', err);
      setError(err.message);
      setServerData([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow p-6 overflow-y-auto max-h-[385px]">
      <h2 className="text-md font-semibold text-gray-800 mb-4">Orders by Server</h2>

      {loading && <p className="text-sm text-gray-400">Loading...</p>}

      {!loading && error && (
        <p className="text-sm text-red-500">Error: {error}</p>
      )}

      {!loading && !error && serverData.length === 0 && (
        <p className="text-sm text-gray-500">No data available</p>
      )}

      <ul className="space-y-3">
        {serverData.map((s, idx) => (
          <li
            key={idx}
            className="bg-gray-50 rounded-xl p-3 flex items-center justify-between hover:bg-gray-100 transition"
          >
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                <FaUser size={16} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">{s.server_name}</p>
                <p className="text-xs text-gray-500">Server</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-gray-900">{s.total_orders}</p>
              <p className="text-xs text-gray-500">Orders</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ServerOrders;
